import { useRef, useEffect } from 'react';

/**
 * FloatingParticles — 深海漂浮颗粒
 * 
 * 极暗蓝色/青蓝色微粒从海底缓慢上升。
 * 速度非常慢，透明度 0.03~0.12。
 * 数量 20~30 个，不是几百个。
 * 窗口尺寸变化时自动重排。
 */

const PARTICLE_CONFIG = {
  count: 25,
  color: 'rgba(88, 183, 232, 0.08)',
  speedRange: [0.15, 0.4], // px/frame
  sizeRange: [1, 3],
  opacityRange: [0.03, 0.12],
  baseColor: { r: 88, g: 183, b: 232 },
};

export default function FloatingParticles() {
  const containerRef = useRef(null);
  const animRef = useRef(null);
  const particlesRef = useRef([]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const init = () => {
      const rect = container.getBoundingClientRect();
      const W = rect.width;
      const H = rect.height;

      particlesRef.current = Array.from({ length: PARTICLE_CONFIG.count }).map(() => ({
        x: Math.random() * W,
        y: Math.random() * H,
        size: PARTICLE_CONFIG.sizeRange[0] + Math.random() * (PARTICLE_CONFIG.sizeRange[1] - PARTICLE_CONFIG.sizeRange[0]),
        speed: PARTICLE_CONFIG.speedRange[0] + Math.random() * (PARTICLE_CONFIG.speedRange[1] - PARTICLE_CONFIG.speedRange[0]),
        opacity: PARTICLE_CONFIG.opacityRange[0] + Math.random() * (PARTICLE_CONFIG.opacityRange[1] - PARTICLE_CONFIG.opacityRange[0]),
        phase: Math.random() * Math.PI * 2,
        driftSpeed: 0.3 + Math.random() * 0.5,
        driftAmp: 3 + Math.random() * 8,
      }));

      // 设置每个粒子的样式
      const nodes = container.children;
      particlesRef.current.forEach((p, i) => {
        if (nodes[i]) {
          nodes[i].style.left = `${p.x}px`;
          nodes[i].style.top = `${p.y}px`;
          nodes[i].style.width = `${p.size}px`;
          nodes[i].style.height = `${p.size}px`;
          nodes[i].style.opacity = p.opacity;
        }
      });
    };

    init();

    let lastTime = performance.now();

    const animate = (now) => {
      const dt = (now - lastTime) / 16.67; // normalize to ~60fps
      lastTime = now;
      const rect = container.getBoundingClientRect();
      const H = rect.height;
      const W = rect.width;

      const nodes = container.children;
      particlesRef.current.forEach((p, i) => {
        // 缓慢上升
        p.y -= p.speed * dt;

        // 左右漂移
        p.x += Math.sin(now / 1000 * p.driftSpeed + p.phase) * p.driftAmp * dt * 0.05;

        // 超出顶部时从底部重新出现
        if (p.y < -10) {
          p.y = H + 10;
          p.x = Math.random() * W;
        }

        if (nodes[i]) {
          nodes[i].style.transform = `translate3d(${p.x}px, ${p.y}px, 0)`;
        }
      });

      animRef.current = requestAnimationFrame(animate);
    };

    animRef.current = requestAnimationFrame(animate);

    const handleResize = () => {
      // 窗口尺寸变化时重新分布
      const rect = container.getBoundingClientRect();
      particlesRef.current.forEach((p) => {
        p.x = Math.random() * rect.width;
        p.y = Math.random() * rect.height;
      });
    };
    window.addEventListener('resize', handleResize);

    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const color = `rgba(${PARTICLE_CONFIG.baseColor.r}, ${PARTICLE_CONFIG.baseColor.g}, ${PARTICLE_CONFIG.baseColor.b}, 1)`;

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 6,
        overflow: 'hidden',
        opacity: 0.8,
      }}
    >
      {Array.from({ length: PARTICLE_CONFIG.count }).map((_, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            borderRadius: '50%',
            background: color,
            filter: 'blur(1px)',
            willChange: 'transform',
          }}
        />
      ))}
    </div>
  );
}
